/**
 * The invoice list, in two shapes.
 *
 * Flat (the default) is what the company-wide debt screen needs: every unpaid
 * invoice visible at once, newest due date first. The customer card renders
 * `customer-invoices.tsx` instead, which passes `groupByPeriod` and gets the
 * year → month tree (invoices-tree.tsx), where a customer's years of history
 * are readable and each branch carries its own sums — plus the row actions
 * below, forwarded straight through to the tree.
 */
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Text,
  ResourceList,
  ResourceItem,
  Button,
  Modal,
} from "@shopify/polaris";
import { EyeIcon } from "@heroicons/react/24/outline";
import { Invoice, InvoiceProduct } from "types/Invoice";
import Currency from "components/format/currency/currency";
import PrettyDate from "components/format/PrettyDate";
import DataTable from "components/datatable/Datatable";
import useIsPhone from "features/files/use-is-phone";
import InvoicesTree from "./invoices-tree";
import { formatInvoicePublicNumber } from "utils/invoice-number";

type Props = {
  invoices: Invoice[];
  /** Customer card only — see the file comment. */
  groupByPeriod?: boolean;
  /** Passed straight through to the tree; no effect in flat mode. */
  onOpenInvoice?: (invoice: Invoice) => void;
  onViewPdf?: (fileId: string) => void;
  getDownloadUrl?: (fileId: string) => string;
};

const Invoices = ({
  invoices,
  groupByPeriod = false,
  onOpenInvoice,
  onViewPdf,
  getDownloadUrl,
}: Props) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);
  const { t } = useTranslation();
  const isPhone = useIsPhone();
  const resourceName = {
    singular: t("invoice"),
    plural: t("invoices"),
  };

  const handleOpenProducts = (invoice: Invoice) => {
    setSelectedInvoice(invoice);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setSelectedInvoice(null);
    setIsModalOpen(false);
  };

  const renderProductsList = (products: InvoiceProduct[] = []) => {
    if (!products.length) {
      return <Text as="p">{t("no_data")}</Text>;
    }

    const collection = products.map((product, index) => ({
      id: `${product.productTitle}-${index}`,
      productTitle: product.productTitle,
      sku: product.sku || "—",
      supplier_sku: product.supplier_sku || "—",
      quantity: product.quantity,
      price: product.price,
      totalPrice: product.totalPrice,
    }));

    return (
      <DataTable
        collection={collection}
        columns={[
          { key: "#" },
          {
            key: "productTitle",
            name: t("product_name"),
            mobileView: "topLeft",
          },
          { key: "sku", name: t("sku") },
          { key: "supplier_sku", name: t("supplier_sku") },
          {
            key: "quantity",
            type: "number",
            name: t("quantity"),
            isDisabledTotal: true,
            mobileView: "bottomLeft",
          },
          {
            key: "price",
            type: "currency",
            name: t("price"),
            isDisabledTotal: true,
            mobileView: "bottomRight",
          },
          {
            key: "totalPrice",
            type: "currency",
            name: t("total_amount"),
            mobileView: "topRight",
          },
        ]}
      />
    );
  };

  const productsModal = (
    <Modal
      open={isModalOpen}
      onClose={handleCloseModal}
      title={t("invoice_products")}
      primaryAction={{
        content: t("close"),
        onAction: handleCloseModal,
      }}
    >
      <Modal.Section>
        {renderProductsList(selectedInvoice?.products || [])}
      </Modal.Section>
    </Modal>
  );

  if (groupByPeriod) {
    return (
      <>
        <InvoicesTree
          invoices={invoices}
          onOpenProducts={handleOpenProducts}
          onOpenInvoice={onOpenInvoice}
          onViewPdf={onViewPdf}
          getDownloadUrl={getDownloadUrl}
        />
        {productsModal}
      </>
    );
  }

  if (isPhone) {
    return (
      <>
        <ResourceList
          resourceName={resourceName}
          items={invoices}
          renderItem={(item) => {
            const {
              id,
              date,
              customerName,
              _amount,
              _paidAmount,
              paymentDueDate,
              products,
            } = item;
            const _debt = _amount - _paidAmount;
            const numberLabel = formatInvoicePublicNumber(item) || item.number;

            return (
              <ResourceItem
                id={id}
                onClick={() => {
                  if (products?.length) handleOpenProducts(item);
                }}
              >
                <div className="layout-row justify-space">
                  <div>{customerName}</div>
                  <Text variant="bodyMd" fontWeight="bold" as="h3" numeric>
                    {numberLabel}
                  </Text>
                </div>
                <div className="layout-row justify-space">
                  <Text as="span" alignment="end" numeric>
                    {<Currency value={_amount} isSymbol={true} />}
                  </Text>
                  {
                    <Text as="span" alignment="end" numeric>
                      {t("debt")}: <Currency value={_debt} isSymbol={true} />
                    </Text>
                  }
                </div>
                <div className="layout-row justify-space">
                  <PrettyDate dateString={date} />
                  <PrettyDate dateString={paymentDueDate} />
                </div>
                {!!products?.length && (
                  <div className="mt-2">
                    <Button
                      size="slim"
                      onClick={() => handleOpenProducts(item)}
                    >
                      {t("view_products")}
                    </Button>
                  </div>
                )}
              </ResourceItem>
            );
          }}
        />
        {productsModal}
      </>
    );
  }

  return (
    <>
      <DataTable
        collection={invoices}
        columns={[
          { key: "#" },
          {
            key: "number",
            name: t("number"),
            type: "custom",
            render: (item: Invoice) => formatInvoicePublicNumber(item) || item.number,
          },
          { key: "date", type: "date", name: t("date") },
          { key: "customerName", name: t("customer") },
          { key: "_amountWithVat", type: "currency", name: t("totalWithVat") },
          { key: "_amount", type: "currency", name: t("totalWithoutVat") },
          { key: "_debt", type: "currency", name: t("debt") },
          { key: "paymentDueDate", type: "date", name: t("payment_due_date") },
          {
            key: "view_products",
            name: t("products"),
            type: "actionBtn",
            Icon: () => <EyeIcon className="w-4 h-4" />,
            onClick: (invoice: Invoice) => handleOpenProducts(invoice),
          },
        ]}
      />
      {productsModal}
    </>
  );
};

export default Invoices;
